import CssBaseline from "@mui/material/CssBaseline"
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles"
import { OptimizelyProvider } from "@optimizely/react-sdk"
import "@tanstack/react-table"
import { RowData } from "@tanstack/react-table"
import EnvironmentDebug from "common/components/environmentDebug/EnvironmentDebug"
import ErrorBoundary from "common/components/errorBoundary/ErrorBoundary"
import AppHead from "common/components/head/Head"
import useProfile from "common/hooks/swr/useProfile"
import initGTMService from "common/services/gtm"
import { optimizely } from "common/services/optimizely"
import Layout from "layouts/layout"
import { swrDefaults } from "lib/swr"
import { AppProps } from "next/app"
import dynamic from "next/dynamic"
import "public/slick/slick-theme.css"
import "public/slick/slick.css"
import { useEffect } from "react"
import { hotjar } from "react-hotjar"
import { RecoilRoot } from "recoil"
import logger from "services/logger"
import { SWRConfig } from "swr"
import theme from "themes/defaultTheme"

const IdpAuthorization = dynamic(
    () => import("components/auth/IdpAuthorization"),
    {
        ssr: false,
    }
)

declare module "@tanstack/table-core" {
    interface TableMeta<TData extends RowData> {
        fixedColumn: boolean
    }
}

const AppBody = ({ children }: { children: React.ReactNode }) => {
    const { data: profile } = useProfile()

    return (
        <>
            {profile && (
                <OptimizelyProvider
                    optimizely={optimizely}
                    timeout={500}
                    user={{ id: profile?.id }}
                >
                    {children}
                </OptimizelyProvider>
            )}
        </>
    )
}

/**
 * Component to initialize pages to override nextjs default App component
 * Referenced from https://nextjs.org/docs/advanced-features/custom-app
 */
const App = ({ Component, pageProps }: AppProps) => {
    useEffect(() => {
        logger.initialize()
        initGTMService()
        hotjar.initialize(3386459, 6)
    }, [])

    return (
        <>
            <RecoilRoot>
                <SWRConfig
                    value={{ ...swrDefaults, provider: () => new Map() }}
                >
                    <MuiThemeProvider theme={theme}>
                        <CssBaseline />
                        <AppHead />
                        <IdpAuthorization>
                            <AppBody>
                                <ErrorBoundary>
                                    <Layout>
                                        <Component {...pageProps} />
                                        <EnvironmentDebug />
                                    </Layout>
                                </ErrorBoundary>
                            </AppBody>
                        </IdpAuthorization>
                    </MuiThemeProvider>
                </SWRConfig>
            </RecoilRoot>
        </>
    )
}

export default App
